<template>
    <div>
        <v-dialog
            :persistent="!confirmedClose"
            @click:outside="initCloseModal(false)"
            v-model="internalValue"
            max-width="700px"
        >
            <v-card>
                <v-card-title>Sync Slideshow and Music</v-card-title>

                <v-card-text>
                    <span>
                        Ensure your music matches or exceeds the video length. Excess audio will be trimmed at the end.
                    </span>

                    <v-divider></v-divider>

                    <div class="d-flex align-center">
                        <span class="mr-4 overline">Auto Sync</span>
                        <v-switch
                            color="#ff7837"
                            class="p-0 m-0"
                            inset
                            hide-details
                            @change="confirmedClose = false"
                            v-model="internalAutoSlideDuration"
                            >Auto Slide Duration</v-switch
                        >

                        <CustomTooltip :tooltipProps="{ top: true, maxWidth: '300px' }">
                            <template #activator>
                                <font-awesome-icon
                                    style="color: #1877f2"
                                    icon="fa-regular fa-info-circle"
                                ></font-awesome-icon>
                            </template>

                            <template #title> Auto Sync </template>

                            <template #content>
                                <span>
                                    Auto Sync range is between
                                    {{ minAutoSlideDuration }}s - {{ maxAutoSlideDuration }}s per slide
                                </span>
                            </template>
                        </CustomTooltip>
                        <!-- <span>est auto duration: {{ calculatedAutoSlideDuration }}</span> -->
                        <template v-if="state.isMobile">
                            <v-spacer />
                            <div class="mobile-slide-count" >
                                <h6 class="m-0 ml-1" >{{ totalSlides }} <span>Slides</span></h6>
                            </div>
                        </template>
                    </div>

                    <div 
                        class="d-flex justify-center flex-sm-row mt-5 sync-wrapper"
                        :class="[ !state.isMobile ? 'flex-column flex-wrap' : '']"
                    >
                        <div :class="['main-stat', 'bordered', { disabled: internalAutoSlideDuration }]">
                            <div class="d-flex justify-space-between">
                                <h1 :key="inputRefreshKey" style="min-width: 120px" class="m-0 py-1">
                                    <!-- {{ internalSlideDuration.toFixed(2) }} -->
                                    <v-text-field
                                        solo
                                        flat
                                        hide-details
                                        dense
                                        type="number"
                                        step=".25"
                                        :min="minSlideDuration"
                                        :max="maxSlideDuration"
                                        hide-spin-buttons
                                        :disabled="internalAutoSlideDuration"
                                        class="duration-input"
                                        v-model="internalSlideDuration"
                                        @blur="handleDurationInputBlur"
                                    ></v-text-field>
                                </h1>
                                <div class="d-flex flex-column">
                                    <div
                                        @mousedown="startUpdatingSlideDuration('up')"
                                        @mouseup="stopUpdatingSlideDuration()"
                                        @mouseleave="stopUpdatingSlideDuration()"
                                        :class="['pointer', 'duration-btn', { disabled: internalAutoSlideDuration }]"
                                    >
                                        <font-awesome-icon icon="fa-solid fa-caret-up"></font-awesome-icon>
                                    </div>
                                    <div
                                        @mousedown="startUpdatingSlideDuration('down')"
                                        @mouseup="stopUpdatingSlideDuration()"
                                        @mouseleave="stopUpdatingSlideDuration()"
                                        :class="['pointer', 'duration-btn', { disabled: internalAutoSlideDuration }]"
                                    >
                                        <font-awesome-icon icon="fa-solid fa-caret-down"></font-awesome-icon>
                                    </div>
                                </div>
                            </div>
                            <span>Seconds / Slide</span>
                        </div>

                        <div class="main-stat bordered" v-if="!state.isMobile">
                            <h1 class="m-0">{{ totalSlides }}</h1>
                            <span>Slides</span>
                        </div>

                        <div class="main-stat bordered">
                            <h1 class="m-0">
                                <v-progress-circular v-if="calculatingNewDuration" indeterminate></v-progress-circular>
                                <span v-else>
                                    {{ internalVideoDuration | formatTimeStamp }}
                                </span>
                            </h1>
                            <span> Video Length</span>
                        </div>
                    </div>

                    <div class="my-5">
                        <div class="d-flex justify-space-between">
                            <strong>Video</strong>
                            <span>{{ internalVideoDuration | formatTimeStamp }}</span>
                        </div>
                        <v-progress-linear
                            dark
                            rounded
                            height="25"
                            :value="playlistDurationPercent"
                            :color="playlistDurationPercent >= 100 ? '#10b981' : '#1877f2'"
                        >
                            <div>
                                <span
                                    >Music <strong>{{ playlistDuration | formatTimeStamp }}</strong></span
                                >
                            </div>
                        </v-progress-linear>
                    </div>

                    <v-alert v-if="showDurationWarning" text color="warning">
                        <template #prepend>
                            <font-awesome-icon class="mr-2" icon="fa-solid fa-circle-exclamation"></font-awesome-icon>
                        </template>
                        Video is longer than music length</v-alert
                    >

                    <v-alert v-if="!showDurationWarning" text color="#10b981">
                        <template #prepend>
                            <font-awesome-icon class="mr-2" icon="fa-solid fa-circle-check"></font-awesome-icon>
                        </template>
                        The music will fade at {{ internalVideoDuration | formatTimeStamp }}</v-alert
                    >

                    <v-alert v-if="internalSlideDuration > 12" text color="warning">
                        <template #prepend>
                            <font-awesome-icon class="mr-2" icon="fa-solid fa-circle-exclamation"></font-awesome-icon>
                        </template>

                        Slides longer than 12 seconds may feel slow to viewers. For memorial services or in-person
                        viewing where longer display times work well, try our
                        <v-btn
                            @click="openTab(`/tribute-slideshow/${$route.params.slug}`)"
                            color="warning"
                            depressed
                            x-small
                            >Service Viewer</v-btn
                        >
                        instead.
                    </v-alert>
                </v-card-text>
                <v-card-actions class="d-flex justify-end">
                    <v-btn class="mr-2 no-text-transform" @click="initCloseModal(false)" depressed outlined
                        >Cancel</v-btn
                    >
                    <v-btn
                        class="no-text-transform"
                        :disabled="disableUpdateBtn"
                        :dark="!disableUpdateBtn"
                        :loading="loading"
                        @click="submitUpdateRenderSettings"
                        depressed
                        color="#ff7837"
                        >Apply</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="closeWithoutSavingModal" max-width="400">
            <v-card>
                <v-card-title> Close without saving? </v-card-title>
                <v-card-text> Click confirm to close without saving changes. </v-card-text>

                <v-card-actions class="d-flex justify-end">
                    <v-btn class="no-text-transform" outlined @click="closeWithoutSavingModal = false" depressed
                        >Cancel</v-btn
                    >
                    <v-btn class="no-text-transform" @click="confirmCloseWithoutSaving" depressed color="error"
                        >Confirm</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import moment from 'moment';
import { playlistTypes } from '@/constants';
import TributeVideoService from '@/services/tributeVideo.service';
import { debounce } from 'lodash';
import { mapActions } from 'pinia';
import CustomTooltip from '../../ui/CustomTooltip.vue';
import { useTributeVideoStore } from '@/stores/tributeVideo';
import { useGlobalState } from '@/stores/globalState';

export default {
    setup() {
        return {
            tributeVideoStore: useTributeVideoStore(),
        }
    },
    data() {
        return {
            playlistTypes,
            closeWithoutSavingModal: false,
            internalSlideDuration: 6,
            internalVideoDuration: 0,
            internalAutoSlideDuration: false,
            apiService: null,
            calculatingNewDuration: false,
            loading: true,
            formData: {
                autoSlideDuration: false,
                animation: 1,
                slideDuration: 6,
                transition: 0,
            },
            awaitingPreviewRefresh: false,
            updateInterval: null,
            updateDelayTimeout: null,
            maxSlideDuration: 60,
            minSlideDuration: 5,
            minAutoSlideDuration: 5,
            maxAutoSlideDuration: 12,
            calculatedAutoSlideDuration: 0,
            calculatedAutoSlideVideoDuration: 0,
            cachedManualSlideDuration: 0,
            cachedManualSlideVideoDuration: 0,
            confirmedClose: true,
            inputRefreshKey: 0,
        };
    },
    inject: ['state'],
    components: { CustomTooltip },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        playlistType: {
            type: Number,
            default: 0,
        },
    },
    computed: {
        internalValue: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            },
        },
        disableUpdateBtn() {
            return this.calculatingNewDuration || this.loading;
        },
        totalSlides() {
            return this.tributeVideoStore.totalSlidesCount;
        },
        totalTributeDuration() {
            return Math.ceil(this.tributeVideoStore.totalDuration);
        },
        slideDuration() {
            return this.tributeVideoStore.slideDuration;
        },
        autoSlideDuration() {
            return this.tributeVideoStore.autoSlideDuration;
        },
        publicPlaylist: {
            get() {
                return this.tributeVideoStore.publicPlaylist;
            },
        },
        originalPlaylist: {
            get() {
                return this.tributeVideoStore.originalPlaylist;
            },
        },
        slides: {
            get() {
                return this.playlistType === this.playlistTypes.PUBLIC ? this.publicPlaylist : this.originalPlaylist;
            },
        },
        playlistDuration() {
            return this.slides.reduce((total, slide) => {
                if (slide.duration) {
                    return total + slide.duration;
                }
                return total;
            }, 0);
        },
        playlistDurationPercent() {
            if (this.internalVideoDuration <= 0) return 0;

            return Math.round((this.playlistDuration / this.internalVideoDuration) * 100);
        },
        videoDurationPercent() {
            return Math.round((this.internalVideoDuration / this.playlistDuration) * 100);
        },
        tributeId() {
            return this.tributeVideoStore.id;
        },
        showDurationWarning() {
            // return Math.ceil(this.playlistDuration) < Math.ceil(this.internalVideoDuration);

            return this.playlistDurationPercent < 100;
        },
    },
    filters: {
        formatTimeStamp(seconds) {
            const duration = moment.duration(seconds, 'seconds');
            const hours = duration.hours();
            const minutes = duration.minutes();
            const secs = duration.seconds();

            const formattedMinutes = String(minutes);
            const formattedSeconds = String(secs).padStart(2, '0');
            if (hours > 0) {
                return `${hours}:${formattedMinutes.padStart(2, '0')}:${formattedSeconds}`;
            }
            return `${formattedMinutes}:${formattedSeconds}`;
        },
    },
    watch: {
        internalValue(newVal) {
            this.refreshInternalValues();
        },
        internalSlideDuration(newVal) {
            if (!newVal) return;
            if (newVal >= this.maxSlideDuration) {
                this.stopUpdatingSlideDuration();
            }
            if (newVal <= this.minSlideDuration) {
                this.stopUpdatingSlideDuration();
            }

            if (newVal >= this.minSlideDuration && newVal <= this.maxSlideDuration) {
                this.cachedManualSlideDuration = this.internalSlideDuration;

                this.debouncedGetJsonWithSlideDuration();
            }
        },
        internalAutoSlideDuration(newVal) {
            if (newVal) {
                this.internalVideoDuration = Math.ceil(this.calculatedAutoSlideVideoDuration);
                this.internalSlideDuration = this.calculatedAutoSlideDuration;
            } else {
                this.internalVideoDuration = Math.ceil(this.cachedManualSlideVideoDuration);
                this.internalSlideDuration = this.cachedManualSlideDuration;
            }
        },
    },
    methods: {
        ...mapActions(useGlobalState, ['showSnackbar']),
        startUpdatingSlideDuration(direction) {
            if (this.internalAutoSlideDuration) return;
            this.stopUpdatingSlideDuration();
            this.confirmedClose = false;

            this.updateSlideDuration(direction);

            this.updateDelayTimeout = setTimeout(() => {
                this.updateInterval = setInterval(() => {
                    this.updateSlideDuration(direction);
                }, 150);
            }, 500);
        },
        handleDurationInputBlur() {
            this.validateInput();
        },
        validateInput() {
            this.internalSlideDuration = Math.round(this.internalSlideDuration * 100) / 100;

            if (this.internalSlideDuration < this.minSlideDuration) {
                this.internalSlideDuration = this.minSlideDuration;
            }

            if (this.internalSlideDuration > this.maxSlideDuration) {
                this.internalSlideDuration = this.maxSlideDuration;
            }

            this.inputRefreshKey++;
        },
        openTab(url) {
            window.open(url, '_blank');
        },
        stopUpdatingSlideDuration() {
            clearTimeout(this.updateDelayTimeout);
            clearInterval(this.updateInterval);
            this.updateInterval = null;
            this.updateUpdateDelayTimeout = null;
        },
        adjustToIncrement(num, dir, incr) {
            if (dir === 'up') {
                return Math.ceil(num / incr) * incr;
            } else {
                return Math.floor(num / incr) * incr;
            }
        },
        updateSlideDuration(direction) {
            if (this.internalSlideDuration == this.minSlideDuration && direction === 'down') return;
            if (this.internalSlideDuration == this.maxSlideDuration && direction === 'up') return;
            const adjustmentAmount = 0.25;

            if (this.internalSlideDuration % adjustmentAmount !== 0) {
                this.internalSlideDuration = this.adjustToIncrement(
                    this.internalSlideDuration,
                    direction,
                    adjustmentAmount,
                );
            } else {
                switch (direction) {
                    case 'up':
                        this.internalSlideDuration += adjustmentAmount;
                        break;
                    case 'down':
                        this.internalSlideDuration -= adjustmentAmount;
                        break;
                    default:
                        console.log('Unhandled slide duration update');
                        break;
                }
            }

            this.cachedManualSlideDuration = this.internalSlideDuration;
        },

        initCloseModal(skipConfirm) {
            if (skipConfirm || this.confirmedClose) {
                this.confirmedClose = true;
                this.internalValue = false;
            } else {
                this.closeWithoutSavingModal = true;
            }
        },
        confirmCloseWithoutSaving() {
            this.closeWithoutSavingModal = false;
            this.internalValue = false;
        },
        refreshInternalValues() {
            console.log('refreshing');
            this.validateInput();
            this.internalSlideDuration = this.slideDuration;
            this.internalVideoDuration = this.totalTributeDuration;
            this.internalAutoSlideDuration = this.autoSlideDuration;

            this.cachedManualSlideDuration = this.internalSlideDuration;
            this.cachedManualSlideVideoDuration = this.totalTributeDuration;

            this.confirmedClose = true;
            this.getRenderSettings();
            this.getJsonWithAutoSlideDuration();
            this.loading = false;

            this.awaitingPreviewRefresh = false;
        },
        async setAuthToken() {
            if (this.state.token) {
                this.token = this.state.token;
            } else {
                const response = await this.$auth.getIdTokenClaims();
                this.token = response.__raw;
            }
        },
        async getJsonWithSlideDuration() {
            try {
                if (!this.tributeId) {
                    throw new Error('Invalid tribute id');
                }

                this.calculatingNewDuration = true;

                const data = {
                    slideDuration: this.internalSlideDuration,
                    autoSlideDuration: false,
                };

                const resp = await this.apiService.getPreviewJsonWithRenderSettings(this.tributeId, data);

                if (resp.data.duration) {
                    this.internalVideoDuration = Math.ceil(resp.data.duration);
                    this.cachedManualSlideVideoDuration = this.internalVideoDuration;
                }
            } catch (error) {
                console.log(error, 'error fetching preview json w/ settings');
            } finally {
                this.calculatingNewDuration = false;
            }
        },
        async getJsonWithAutoSlideDuration() {
            try {
                if (!this.tributeId) {
                    throw new Error('Invalid tribute id');
                }

                this.calculatingNewDuration = true;

                const data = {
                    autoSlideDuration: true,
                };

                const resp = await this.apiService.getPreviewJsonWithRenderSettings(this.tributeId, data);
                if (resp.data.slideDuration) {
                    this.calculatedAutoSlideDuration = resp.data.slideDuration;
                }
                if (resp.data.duration) {
                    this.calculatedAutoSlideVideoDuration = resp.data.duration;
                }
            } catch (error) {
                console.log(error, 'error fetching preview json w/ settings');
            } finally {
                this.calculatingNewDuration = false;
            }
        },
        async getRenderSettings() {
            try {
                if (!this.tributeId) {
                    throw new Error('invalid tribute id');
                }

                const resp = await this.apiService.getRenderSettings(this.tributeId);

                if (resp.data.settings) {
                    this.formData = { ...resp.data.settings };

                    console.log(this.formData, 'form data mount');

                    this.cachedManualSlideDuration = this.formData.slideDuration;
                    this.internalSlideDuration = this.formData.slideDuration;

                    if (
                        this.internalSlideDuration < this.minSlideDuration &&
                        this.internalSlideDuration > this.maxSlideDuration
                    ) {
                        this.internalSlideDuration = 6;
                        this.debouncedGetJsonWithSlideDuration();
                    }
                }
            } catch (error) {
                console.log(error, 'error fetching render settings');
            }
        },
        async submitUpdateRenderSettings() {
            this.validateInput();

            if (this.internalAutoSlideDuration) {
                await this.setAutoSlideDuration(this.internalAutoSlideDuration);
            } else {
                await this.submitManualSlideDuration();
            }

            this.initCloseModal(true);
        },
        async submitManualSlideDuration() {
            try {
                if (!this.tributeId) {
                    throw new Error('Invalid tribute id');
                }

                this.loading = true;
                const data = {
                    ...this.formData,
                    slideDuration: this.internalSlideDuration,
                    autoSlideDuration: false,
                };

                const resp = await this.apiService.updateRenderSettings(this.tributeId, data);

                if (resp?.data?.settings?.settings) {
                    this.formData = resp?.data?.settings?.settings;

                    this.tributeVideoStore.updateTributeVideo({
                        autoSlideDuration: resp.data.settings.settings.autoSlideDuration,
                        slideDuration: resp.data.settings.settings.slideDuration,
                        selectedAnimation: resp.data.settings.settings.animation,
                        selectedTransition: resp.data.settings.settings.transition,
                    });
                }

                if (resp?.data?.renderJson) {
                    this.tributeVideoStore.updateTributeVideo({
                        totalDuration: resp.data.renderJson.duration,
                    });
                }

                this.showSnackbar({ message: 'Slide duration settings updated' });
            } catch (error) {
                console.log(error, 'error updateing tribute render settings');
                this.showSnackbar({ message: 'Error saving slide duration settings', color: 'error' });
            } finally {
                this.loading = false;
            }
        },

        async setAutoSlideDuration(bool) {
            try {
                if (!this.tributeId) {
                    throw new Error('Invalid tribute id');
                }

                this.loading = true;
                const data = {
                    ...this.formData,
                    autoSlideDuration: bool,
                };

                const resp = await this.apiService.updateRenderSettings(this.tributeId, data);

                if (resp?.data?.settings?.settings) {
                    this.formData = resp?.data?.settings?.settings;

                    this.tributeVideoStore.updateTributeVideo({
                        autoSlideDuration: resp.data.settings.settings.autoSlideDuration,
                        slideDuration: resp.data.settings.settings.slideDuration,
                        selectedAnimation: resp.data.settings.settings.animation,
                        selectedTransition: resp.data.settings.settings.transition,
                    });
                }

                if (resp?.data?.renderJson) {
                    this.tributeVideoStore.updateTributeVideo({
                        totalDuration: resp.data.renderJson.duration,
                    });
                }
            } catch (error) {
                console.log(error, 'error settings auto slide duration');

                this.showSnackbar({ message: 'Error setting slide auto duration', color: 'error' });
            } finally {
                this.loading = false;
            }
        },
    },
    created() {
        this.debouncedGetJsonWithSlideDuration = debounce(this.getJsonWithSlideDuration, 400);
        this.debouncedRefreshInternalValues = debounce(this.refreshInternalValues, 1000);
    },
    async mounted() {
        await this.setAuthToken();

        this.apiService = TributeVideoService(this.token);

        this.getRenderSettings();
        this.getJsonWithAutoSlideDuration();
        this.loading = false;
    },
    sockets: {
        NotifyTributeSongsUpdate(data) {
            if (this.tributeId && data.tributeId === this.tributeId) {
                this.awaitingPreviewRefresh = true;
            }
        },
        async NotifyTributeJsonRefreshed(data) {
            if (!this.awaitingPreviewRefresh) return;

            if (this.tributeId && data.id == this.tributeId) {
                this.debouncedRefreshInternalValues();
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.sync-wrapper {
    gap: 24px;
}
.main-stat {
    padding: 8px 24px;
    border-radius: 4px;
    color: #2c3e50;
    text-align: center;
    user-select: none;
    height: fit-content;
    max-width: 33%;
}

.disabled {
    color: $medium-gray !important;
    cursor: not-allowed !important;
}

.bordered {
    border: 1px solid $medium-gray;
}

.duration-btn {
    margin-left: 4px;
    padding: 0 8px;
    border-radius: 4px;
}

.duration-btn:hover {
    background-color: #f2f2f2;
}

.duration-btn.disabled {
    background-color: white;
}

.duration-input {
    font-size: 40px;
    width: 100%;
    padding: 0;
    margin: 0;
    display: flex;
}
@include mobile() {
    .sync-wrapper {
        gap: 10px;
        flex-direction: row !important;
        flex-wrap: nowrap !important;
    }
    .main-stat {
        max-width: none !important;
    }
    .main-stat h1, 
    .duration-input {
        font-size: 1.3rem;
    }
    .mobile-slide-count {
        text-align: right;
    }
}
</style>
